import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import { useTranslation } from 'react-i18next';
import {
  CATEGORY_SORTING_VALUES,
  DataRoomRFI,
  FileTypeRFI,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import { Button, Collapsible } from 'ncoded-component-library';
import CategoryInstance from '../CategoryInstance';
import {
  getIsRfiFileStatusUploaded,
  getIsRfiStatusClosed,
  getIsRfiStatusPending,
} from 'router/subrouters/DataRoom/utils';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import ClipboardcopyIcon from 'icons/Clipboardcopy.icon';
import { getResolveUrl, getRFILinkRoute } from 'utils';
import MessagesFromUploader from '../MessagesFromUploader';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useRfiCategory from 'hooks/useRfiCategory';
import Counter from 'components/Counter';

import './RfiInstance.styles.scss';

type RfiInstanceProps = {
  className?: string;
  dataRoomRfi: DataRoomRFI;
  onAddFileToDr: (categoryId: string, file: FileTypeRFI) => void;
  onAddAllFilesToDr: () => void;
  onSeeMore?: () => void;
};

const RfiInstance: React.FC<RfiInstanceProps> = (props) => {
  const {
    className,
    dataRoomRfi,
    onAddFileToDr,
    onAddAllFilesToDr,
    onSeeMore,
  } = props;

  const [sort, setSort] = useState<string | undefined>();

  const { idsOfUpdatedSubmitted, sortFiles, viewRfi } = useContext(RfiContext);
  const { doesntHaveAccess } = useContext(DataRoomContext);

  const classes = classNames('rfi-instance', 'white-panel', className);

  const {
    _id,
    name,
    dataRoomRFICategories,
    status,
    externalKey,
    brokerMessages,
  } = dataRoomRfi;

  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard();

  const hasSort = useMemo(() => !getIsRfiStatusPending(status), [status]);

  const { getRequestedAndAdditionalCategory } = useRfiCategory();

  const { requestedDataRoomRFICategory, additionalDataRoomRFICategory } =
    getRequestedAndAdditionalCategory(dataRoomRFICategories);

  const categories = useMemo(
    () => [...requestedDataRoomRFICategory, additionalDataRoomRFICategory],
    [additionalDataRoomRFICategory, requestedDataRoomRFICategory],
  );

  const isNew = idsOfUpdatedSubmitted.includes(_id);

  const rfiTrigger = useMemo(() => {
    const hasAddAllFiles = dataRoomRFICategories
      .flatMap(({ files }) => files)
      .some(({ status }) => getIsRfiFileStatusUploaded(status));

    return (
      <div className="trigger" {...(isNew && { onClick: () => viewRfi(_id) })}>
        <div>
          <p className="rfi-name">
            {isNew && <Counter onlyDot />}
            {name}
          </p>

          {!doesntHaveAccess && hasAddAllFiles && (
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onAddAllFilesToDr();
              }}
            >
              {t('addAllFilesToDR')}
            </Button>
          )}

          {!doesntHaveAccess && onSeeMore && (
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onSeeMore();
              }}
            >
              {t('seeMore')}
            </Button>
          )}
        </div>
        <ChevrondownIcon />
      </div>
    );
  }, [
    _id,
    isNew,
    dataRoomRFICategories,
    name,
    doesntHaveAccess,
    viewRfi,
    onAddAllFilesToDr,
    onSeeMore,
    t,
  ]);

  const updateSort = useCallback(
    (sort: string) => {
      setSort(sort);
      sortFiles(_id, sort);
    },
    [_id, sortFiles],
  );

  return (
    <Collapsible className={classes} trigger={rfiTrigger}>
      {!getIsRfiStatusClosed(status) && (
        <p className="share-link">
          {t('shareRfiLinkWithRecipients')}
          <Button
            variant="icon"
            icon={ClipboardcopyIcon}
            onClick={(e) =>
              copyToClipboard(e, getResolveUrl(getRFILinkRoute(externalKey)))
            }
          />
        </p>
      )}

      {hasSort && (
        <div className="rfi-instance__sort">
          <SortingButton
            content={t('fileName')}
            sortingValue={CATEGORY_SORTING_VALUES.FILE_NAME}
            sort={sort}
            updateSort={updateSort}
          />
          <SortingButton
            content={t('uploader')}
            sortingValue={CATEGORY_SORTING_VALUES.UPLOADER}
            sort={sort}
            updateSort={updateSort}
          />
          <span />
        </div>
      )}

      {categories.map((category) => (
        <CategoryInstance
          key={category._id}
          dataRoomRFICategory={category}
          onAddFileToDr={onAddFileToDr}
        />
      ))}
      <MessagesFromUploader
        rfiId={dataRoomRfi?._id}
        brokerMessages={brokerMessages}
      />
    </Collapsible>
  );
};

export default RfiInstance;
